import {useMutation} from "@tanstack/react-query"
import {Radio, Space, Typography} from "antd"
import moment from "moment"
import {BaseButton} from "components"
import {formatSavedPaymentMethodLabel, handleError, PAYMENT_METHOD_NEW, toastSuccess} from "helpers"
import {useEffect, useMemo, useState} from "react"
import {useTranslation} from "react-i18next"
import {usePaymentProviderConfigs} from "hooks/usePaymentProviderConfigs"
import {useRetailersGatewayCollect, useStudentPaymentMethods} from "hooks"
import {RetailersGatewayPaymentType} from "hooks/useRetailersGatewayCollect"
import {studentPaymentService} from "services"
import {
    CreateRecurringPaymentPayload,
    PaymentProvider,
    RecurringPaymentInterval,
    RetailersGatewayPaymentDetails,
    StudentPaymentSupportedCurrency
} from "types/student-account/student-payment"
import {FormLabel} from "components/Form"

type Props = {
    profileId: number
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    intervalCount?: number
    cancelAt: moment.Moment | null
    paymentPlanItems?: CreateRecurringPaymentPayload["paymentPlanItems"]
    paymentPlanId?: number | null
    onSuccess: () => void
}

type PaymentSource = {paymentMethodId: number} | {paymentToken: string; paymentDetails: RetailersGatewayPaymentDetails}

export const RetailersGatewayRecurringSubscriptionForm = ({
    profileId,
    amountInCents,
    currency,
    interval,
    intervalCount,
    cancelAt,
    paymentPlanItems,
    paymentPlanId,
    onSuccess
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const {retailersGatewayProviderConfig} = usePaymentProviderConfigs(profileId)
    const {isReady, error: collectError, openPaymentForm} = useRetailersGatewayCollect(retailersGatewayProviderConfig)

    const [paymentType, setPaymentType] = useState<RetailersGatewayPaymentType>("card")

    const {paymentMethods: allPaymentMethods, isLoading: isLoadingPaymentMethods} = useStudentPaymentMethods({
        profileId
    })
    const paymentMethods = useMemo(
        () => allPaymentMethods.filter((method) => method.provider === PaymentProvider.RetailersGateway),
        [allPaymentMethods]
    )
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | undefined>()

    // Auto-select the active saved payment method (or the first one) once methods load; falls back
    // to "add new" if the student has no saved Retailers Gateway payment method yet.
    useEffect(() => {
        // Wait for the saved methods to load, so the active one is pre-selected rather than
        // "add new" being locked in from the initial empty list.
        if (isLoadingPaymentMethods) return
        setSelectedPaymentMethodId((prev) => {
            if (prev !== undefined) return prev
            if (!paymentMethods.length) return PAYMENT_METHOD_NEW
            const active = paymentMethods.find((m) => m.isActive) ?? paymentMethods[0]
            return active.paymentMethodId
        })
    }, [paymentMethods, isLoadingPaymentMethods])

    useEffect(() => {
        if (collectError) handleError(collectError)
    }, [collectError])

    const selectedIsNewMethod = selectedPaymentMethodId === undefined || selectedPaymentMethodId === PAYMENT_METHOD_NEW

    const {isLoading, mutate: submit} = useMutation({
        mutationFn: async (paymentSource: PaymentSource) =>
            studentPaymentService.createRetailersGatewayRecurringSubscription({
                profileId,
                amountInCents,
                currency,
                interval,
                intervalCount,
                cancelAt: cancelAt?.toISOString() ?? null,
                paymentPlanItems,
                ...(paymentPlanId ? {paymentPlanId} : {}),
                ...paymentSource
            }),
        onError: (error: any) => handleError(error),
        onSuccess: () => {
            toastSuccess(t("msg.recurringPayment.addedSuccessfully"))
            onSuccess()
        }
    })

    const handleSubmit = () => {
        if (!selectedIsNewMethod && selectedPaymentMethodId) {
            submit({paymentMethodId: selectedPaymentMethodId})
            return
        }
        try {
            // The gateway-hosted lightbox collects the card/bank account; the subscription is only
            // created once it returns a token — closing the lightbox changes nothing.
            openPaymentForm(paymentType, (paymentToken, paymentDetails) => submit({paymentToken, paymentDetails}))
        } catch (error) {
            handleError(error)
        }
    }

    if (amountInCents <= 0) {
        return (
            <Typography.Paragraph type="secondary">
                {t("msg.recurringPayment.enterAmount", "Please enter an amount greater than 0")}
            </Typography.Paragraph>
        )
    }

    return (
        <div>
            <FormLabel label={t("common:label.paymentMethods", "Payment Method")} />
            <Radio.Group
                className="mb-8"
                onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                value={selectedPaymentMethodId}>
                <Space direction="vertical">
                    {paymentMethods.map((method) => (
                        <Radio key={method.paymentMethodId} value={method.paymentMethodId}>
                            {formatSavedPaymentMethodLabel(method)}
                        </Radio>
                    ))}
                    <Radio value={PAYMENT_METHOD_NEW}>
                        {t("common:label.addNewPaymentMethod", "Add new payment method")}
                    </Radio>
                </Space>
            </Radio.Group>

            {selectedIsNewMethod && (
                <Radio.Group className="mb-8 mt-8" value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                    <Radio value="card">Card</Radio>
                    <Radio value="ach">Bank Account (ACH)</Radio>
                </Radio.Group>
            )}

            <BaseButton
                size="small"
                title={t("common:action.add")}
                loading={isLoading}
                disabled={isLoading || (selectedIsNewMethod && !isReady)}
                className="w-full mt-24"
                onClick={handleSubmit}
            />
        </div>
    )
}
